import { firestoreAction as vuexfireAction } from 'vuexfire'
import { authReady } from '@/firebase'

/**
 * Every Firestore read in this app goes through vuexfire's firestoreAction, so
 * wrapping it gives us one place to:
 *
 *   1. hold reads until Firebase sign-in has settled - Firestore now rejects
 *      unauthenticated callers, so a bind issued during app boot would fail,
 *   2. keep track of live listeners so they can all be detached on logout,
 *      because vuexfire offers no "unbind everything" of its own, and
 *   3. re-mint the Firebase session once if a bind is refused.
 *
 * Import this in place of `vuexfire` in any module that binds Firestore refs.
 */

// key -> teardown. vuexfire itself keys subscriptions by the commit function and
// then the state key; the modules here are not namespaced, so keys are unique
// across the whole store.
const listeners = new Map();

// Set by the auth service at load time. Kept as a registration rather than an
// import so this module never has to reach back into the store, which would
// close a require cycle (store -> modules -> here -> auth service -> store).
let reauthenticate = null;

export function setReauthenticator(fn) {
    reauthenticate = fn;
}

function isPermissionDenied(error) {
    return !!error && (error.code === 'permission-denied' || error.code === 7);
}

/**
 * Detach every live Firestore listener. Called on logout: listeners left bound
 * keep firing after sign-out and start throwing permission errors.
 */
export function unbindAllFirestoreRefs() {
    listeners.forEach(teardown => {
        try {
            teardown();
        } catch (error) {
            // A listener that has already gone away must not stop the rest.
        }
    });

    listeners.clear();
}

export function firestoreAction(action) {
    return vuexfireAction(function (context, payload) {
        // Vuex invokes action handlers with `this` bound to the store, and these
        // modules rely on it for `this.$axios`. vuexfire forwards it to us, so we
        // have to forward it on.
        const store = this;

        const tracked = {
            ...context,

            bindFirestoreRef(key, ref, options) {
                listeners.set(key, () => context.unbindFirestoreRef(key));
                return context.bindFirestoreRef(key, ref, options);
            },

            unbindFirestoreRef(key, reset) {
                listeners.delete(key);
                return context.unbindFirestoreRef(key, reset);
            }
        };

        const run = () => action.call(store, tracked, payload);

        return authReady()
            .then(run)
            .catch(error => {
                // The Firebase session has been lost - signed out in another tab,
                // or browser storage cleared. Re-mint once before giving up.
                if (!isPermissionDenied(error) || !reauthenticate) {
                    return Promise.reject(error);
                }

                return reauthenticate().then(run);
            });
    });
}

export default firestoreAction;
